import React, {CSSProperties, ElementRef, Fragment, ReactNode, useMemo, useRef, useState} from 'react';
import {Transition, TransitionGroup, TransitionStatus} from 'react-transition-group';
import {usePrevious} from '../../Hooks';
import {cn, createRequiredContext, cva} from '../../Utilities';

interface TabItem {
    label: string;
    element: ReactNode;
}

type RsnTabsContextValue = {
    tabs: TabItem[];
    activeTab: number;
    previousTab: number;
    setActiveTab: (tab: number) => void;
};

const {RsnTabsProvider, useRsnTabs} = createRequiredContext('RsnTabs')<RsnTabsContextValue>();

/**
 * RsnTabs
 *
 * @example
 * const tabs = [
 *   { label: 'Tab 1', element: <div>First tab content</div> },
 *   { label: 'Tab 2', element: <div>Second tab content</div> }
 * ];
 *
 * return (
 *   <RsnTabs tabs={tabs}>
 *      // You can wrap Triggers and Content with any custom layout elements
 *     <RsnTabs.Triggers />
 *     <RsnTabs.Content />
 *   </RsnTabs>
 * );
 */
export function RsnTabs({tabs, children}: {tabs: TabItem[]; children?: ReactNode}) {
    const [activeTab, setActiveTab] = useState(0);
    const previousTab = usePrevious(activeTab) ?? activeTab;

    return (
        <RsnTabsProvider
            value={{
                tabs,
                activeTab,
                setActiveTab,
                previousTab,
            }}
        >
            {children}
        </RsnTabsProvider>
    );
}

const triggerVariant = cva(
    [
        'fv-text-m-bold flex-1 overflow-hidden text-ellipsis whitespace-nowrap',
        'rounded-xs px-[14px] py-2 text-center text-neutral-100',
    ],
    {
        variants: {
            active: {true: 'bg-shade-10 shadow'},
        },
        defaultVariants: {
            active: false,
        },
    }
);

RsnTabs.Triggers = function Triggers() {
    const {tabs, activeTab, setActiveTab} = useRsnTabs();
    return (
        <div className='relative flex items-center overflow-hidden rounded-sm bg-shade-20 p-0.5'>
            {tabs.map((tab, index) => (
                <Fragment key={index}>
                    {index > 0 && (
                        <hr
                            className={cn('h-5 w-[1px] border-none', {
                                'bg-shade-30': activeTab !== index && activeTab + 1 !== index,
                            })}
                        />
                    )}
                    <button
                        key={index}
                        className={triggerVariant({
                            active: activeTab === index,
                        })}
                        onClick={() => setActiveTab(index)}
                    >
                        {tab.label}
                    </button>
                </Fragment>
            ))}
        </div>
    );
};

RsnTabs.Content = function Content() {
    const {tabs, activeTab, previousTab} = useRsnTabs();

    const direction = useRef(-1);
    direction.current = Math.sign(previousTab - activeTab);

    const refs = useMemo(() => tabs.map(() => ({current: null as ElementRef<'div'> | null})), [tabs.length]);
    const nodeRef = refs[activeTab];

    const duration = 150;

    const baseStyles: React.CSSProperties = {
        position: 'absolute',
        width: '100%',
        transitionProperty: 'transform, opacity',
        transitionDuration: `${duration}ms`,
        transitionTimingFunction: 'ease-in-out',
        transform: 'translateX(0)',
    };

    const statusStyles = (): {[K in TransitionStatus]?: CSSProperties} => ({
        exited: {transform: `translateX(${-40 * direction.current}%)`, opacity: 0},
        entering: {transform: 'translateX(0)', transitionDelay: `${duration / 2}ms`},
        entered: {transform: 'translateX(0)'},
        exiting: {transform: `translateX(${40 * direction.current}%)`, opacity: 0},
    });

    const getTabStyle = (status: TransitionStatus): CSSProperties => ({
        ...baseStyles,
        ...statusStyles()[status],
    });

    return (
        <TransitionGroup component='div' style={{position: 'relative', width: '100%'}}>
            <Transition key={activeTab} nodeRef={nodeRef} timeout={duration} mountOnEnter unmountOnExit>
                {status => (
                    <div key={activeTab} ref={nodeRef} style={getTabStyle(status)}>
                        {tabs[activeTab]?.element}
                    </div>
                )}
            </Transition>
        </TransitionGroup>
    );
};

RsnTabs.Simple = function Simple({tabs}: {tabs: TabItem[]}) {
    return (
        <RsnTabs tabs={tabs}>
            <div>
                <RsnTabs.Triggers />
                <RsnTabs.Content />
            </div>
        </RsnTabs>
    );
};
